/**
 * IndexedDB persistence layer for plugin builder undo/redo history.
 *
 * All operations are wrapped in try/catch so the builder falls back
 * to in-memory history if IndexedDB is unavailable or errors out.
 */

const DB_NAME = 'fias-plugin-builder';
const STORE_NAME = 'undo-history';
const DB_VERSION = 1;
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

interface HistoryRecord {
  sessionId: string;
  states: unknown[];
  index: number;
  lastAccessed: number; // Date.now() timestamp
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'sessionId' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function loadHistory(
  sessionId: string,
): Promise<{ states: unknown[]; index: number } | null> {
  try {
    const db = await openDb();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(sessionId);

      request.onsuccess = () => {
        const record = request.result as HistoryRecord | undefined;
        if (record && Array.isArray(record.states) && typeof record.index === 'number') {
          resolve({ states: record.states, index: record.index });
        } else {
          resolve(null);
        }
      };
      request.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

export async function saveHistory(
  sessionId: string,
  states: unknown[],
  index: number,
): Promise<void> {
  try {
    const db = await openDb();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const record: HistoryRecord = {
        sessionId,
        states,
        index,
        lastAccessed: Date.now(),
      };
      const request = store.put(record);
      request.onsuccess = () => resolve();
      request.onerror = () => resolve(); // swallow — graceful degradation
    });
  } catch {
    // IndexedDB unavailable — silently degrade
  }
}

export async function deleteHistory(sessionId: string): Promise<void> {
  try {
    const db = await openDb();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.delete(sessionId);
      request.onsuccess = () => resolve();
      request.onerror = () => resolve();
    });
  } catch {
    // silently degrade
  }
}

export async function cleanupOldEntries(): Promise<void> {
  try {
    const db = await openDb();
    const cutoff = Date.now() - MAX_AGE_MS;

    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.openCursor();

      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          const record = cursor.value as HistoryRecord;
          if (record.lastAccessed < cutoff) {
            cursor.delete();
          }
          cursor.continue();
        } else {
          resolve();
        }
      };
      request.onerror = () => resolve();
    });
  } catch {
    // silently degrade
  }
}
